import { supabase } from '@/integrations/supabase/client';
import type {
  Gender,
  Match,
  PlayerMatchStats,
  RosterEntry,
  Team,
  TeamCategory,
} from '@/store/clubs';

/** Data-access layer for teams, rosters, matches and player statistics. */

type TeamRow = {
  id: string;
  club_id: string;
  name: string;
  category: string;
  gender: string;
  division: string;
  season: string | null;
  created_at: string;
};

const mapTeam = (row: TeamRow): Team => ({
  id: row.id,
  clubId: row.club_id,
  name: row.name,
  category: row.category as TeamCategory,
  gender: row.gender as Gender,
  division: row.division,
  season: row.season ?? undefined,
  createdAt: row.created_at,
});

export async function fetchTeamsForClubs(clubIds: string[]): Promise<Team[]> {
  if (clubIds.length === 0) return [];
  const { data, error } = await supabase
    .from('teams')
    .select('*')
    .in('club_id', clubIds)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return ((data ?? []) as unknown as TeamRow[]).map(mapTeam);
}

export async function fetchTeamById(teamId: string): Promise<Team | null> {
  const { data, error } = await supabase.from('teams').select('*').eq('id', teamId).maybeSingle();
  if (error) throw error;
  return data ? mapTeam(data as unknown as TeamRow) : null;
}

export async function insertTeam(data: {
  clubId: string;
  name: string;
  category: TeamCategory;
  gender: Gender;
  division: string;
  season?: string;
}): Promise<Team> {
  const { data: row, error } = await supabase
    .from('teams')
    .insert({
      club_id: data.clubId,
      name: data.name.trim(),
      category: data.category,
      gender: data.gender,
      division: data.division,
      season: data.season ?? null,
    })
    .select('*')
    .single();
  if (error) throw error;
  return mapTeam(row as unknown as TeamRow);
}

export async function removeTeamRow(teamId: string) {
  const { error } = await supabase.from('teams').delete().eq('id', teamId);
  if (error) throw error;
}

// ------------------------------------------------------------------ roster

type RosterRow = {
  id: string;
  team_id: string;
  player_id: string;
  jersey_number: number;
  position: string;
  height: string | null;
  age: number | null;
  created_at: string;
};

const mapRoster = (row: RosterRow): RosterEntry => ({
  id: row.id,
  teamId: row.team_id,
  playerId: row.player_id,
  jerseyNumber: row.jersey_number,
  position: row.position,
  height: row.height ?? undefined,
  age: row.age ?? undefined,
  createdAt: row.created_at,
});

export async function fetchRosterForTeams(teamIds: string[]): Promise<RosterEntry[]> {
  if (teamIds.length === 0) return [];
  const { data, error } = await supabase.from('team_members').select('*').in('team_id', teamIds);
  if (error) throw error;
  return ((data ?? []) as unknown as RosterRow[]).map(mapRoster);
}

export async function fetchRosterForPlayer(playerId: string): Promise<RosterEntry[]> {
  const { data, error } = await supabase.from('team_members').select('*').eq('player_id', playerId);
  if (error) throw error;
  return ((data ?? []) as unknown as RosterRow[]).map(mapRoster);
}

export async function insertRosterEntry(data: {
  teamId: string;
  playerId: string;
  jerseyNumber: number;
  position: string;
  height?: string;
  age?: number;
}): Promise<RosterEntry | null> {
  const { data: row, error } = await supabase
    .from('team_members')
    .insert({
      team_id: data.teamId,
      player_id: data.playerId,
      jersey_number: data.jerseyNumber,
      position: data.position,
      height: data.height ?? null,
      age: data.age ?? null,
    })
    .select('*')
    .maybeSingle();
  if (error) {
    if (error.code === '23505') return null;
    throw error;
  }
  return row ? mapRoster(row as unknown as RosterRow) : null;
}

export async function removeRosterRow(entryId: string) {
  const { error } = await supabase.from('team_members').delete().eq('id', entryId);
  if (error) throw error;
}

export async function removePlayerFromTeamRow(teamId: string, playerId: string) {
  const { error } = await supabase
    .from('team_members')
    .delete()
    .eq('team_id', teamId)
    .eq('player_id', playerId);
  if (error) throw error;
}

export async function moveRosterEntry(entryId: string, toTeamId: string) {
  const { error } = await supabase
    .from('team_members')
    .update({ team_id: toTeamId })
    .eq('id', entryId);
  if (error) throw error;
}

// ----------------------------------------------------------------- matches

type MatchRow = {
  id: string;
  team_id: string;
  opponent: string;
  date: string;
  location: string;
  team_score: number | null;
  opponent_score: number | null;
  created_at: string;
};

const mapMatch = (row: MatchRow): Match => ({
  id: row.id,
  teamId: row.team_id,
  opponent: row.opponent,
  date: row.date,
  location: row.location,
  teamScore: row.team_score ?? undefined,
  opponentScore: row.opponent_score ?? undefined,
  createdAt: row.created_at,
});

export async function fetchMatchesForTeam(teamId: string): Promise<Match[]> {
  const { data, error } = await supabase
    .from('matches')
    .select('*')
    .eq('team_id', teamId)
    .order('date', { ascending: true });
  if (error) throw error;
  return ((data ?? []) as unknown as MatchRow[]).map(mapMatch);
}

export async function insertMatch(data: {
  teamId: string;
  opponent: string;
  date: string;
  location: string;
  teamScore?: number;
  opponentScore?: number;
}): Promise<Match> {
  const { data: row, error } = await supabase
    .from('matches')
    .insert({
      team_id: data.teamId,
      opponent: data.opponent,
      date: data.date,
      location: data.location,
      team_score: data.teamScore ?? null,
      opponent_score: data.opponentScore ?? null,
    })
    .select('*')
    .single();
  if (error) throw error;
  return mapMatch(row as unknown as MatchRow);
}

export async function patchMatchScore(matchId: string, teamScore: number, opponentScore: number) {
  const { error } = await supabase
    .from('matches')
    .update({ team_score: teamScore, opponent_score: opponentScore })
    .eq('id', matchId);
  if (error) throw error;
}

// ------------------------------------------------------------------- stats

type StatsRow = {
  id: string;
  match_id: string;
  player_id: string;
  points: number;
  rebounds: number;
  assists: number;
  steals: number;
  blocks: number;
  created_at: string;
};

const mapStats = (row: StatsRow): PlayerMatchStats => ({
  id: row.id,
  matchId: row.match_id,
  playerId: row.player_id,
  points: row.points,
  rebounds: row.rebounds,
  assists: row.assists,
  steals: row.steals,
  blocks: row.blocks,
  createdAt: row.created_at,
});

export async function fetchStatsForMatches(matchIds: string[]): Promise<PlayerMatchStats[]> {
  if (matchIds.length === 0) return [];
  const { data, error } = await supabase
    .from('player_statistics')
    .select('*')
    .in('match_id', matchIds);
  if (error) throw error;
  return ((data ?? []) as unknown as StatsRow[]).map(mapStats);
}

export async function fetchStatsForPlayer(playerId: string): Promise<PlayerMatchStats[]> {
  const { data, error } = await supabase
    .from('player_statistics')
    .select('*')
    .eq('player_id', playerId);
  if (error) throw error;
  return ((data ?? []) as unknown as StatsRow[]).map(mapStats);
}

export async function upsertStats(data: {
  playerId: string;
  matchId: string;
  points: number;
  rebounds: number;
  assists: number;
  steals: number;
  blocks: number;
}): Promise<PlayerMatchStats> {
  const { data: row, error } = await supabase
    .from('player_statistics')
    .upsert(
      {
        player_id: data.playerId,
        match_id: data.matchId,
        points: data.points,
        rebounds: data.rebounds,
        assists: data.assists,
        steals: data.steals,
        blocks: data.blocks,
      },
      { onConflict: 'match_id,player_id' },
    )
    .select('*')
    .single();
  if (error) throw error;
  return mapStats(row as unknown as StatsRow);
}
